// STYLE H10 (docs/STYLE.md): load-time grading that makes the generated art fit one style without re-rendering.
// Runs once per atlas page in IsoArt.load, on the sprite frames (props, trees, cars), before the textures exist.
//   defringe  the image model's magenta keying leaves pink matte pixels on cut-out edges (tree crowns: 3.6% of
//             the elm's edge); an edge pixel with a magenta cast takes the colour of its opaque inside neighbours
//   clamp     a prop may not out-saturate the characters standing next to it (H1.5): per-family ceilings on HSV
//             saturation, so a shrub or a hydrant reads as a shrub and a hydrant, not as loot or a light
// Measured with tools/style/measure.py; the numbers are in STYLE.md H10.

/** per-frame saturation ceiling (HSV S, 0..1). Unlisted frames are left alone. */
export const SAT_CEIL: Record<string, number> = {
  shrub: 0.62, planter: 0.62,                        // foliage: the green was S 0.95 (brighter than any character)
  tree_maple: 0.59, tree_elm: 0.59, tree_ash: 0.59,   // autumn crowns keep the season but drop out of the reserved orange
  // R10-033 (round 9): the yellow elm at S 0.74 put street frames at 17.8% and 23.5% hot px; at 0.59 (and value x0.9) it sits
  // under the band beside the ash and maple and still reads autumn yellow (hue untouched, 40-50)
  // R10-027: the big orange tree by the Black Dog (tree_ash, not a maple) put a street frame at 11.1% hot px. 'Hot' is S >= 0.6,
  // so any ceiling above 0.6 leaves a crown in the band: the orange crowns sit just under it, still orange
  hydrant: 0.72, mailbox: 0.70,                       // signal colours stay readable as red, not as danger
  lowtable: 0.55, table2: 0.55, pooltable: 0.62, tv: 0.70,          // furniture next to people
};

/** per-frame value SCALE beside the saturation ceiling (R10-027): under S 0.59 a bright crown went peach; a step down in value
 *  keeps it a deep autumn orange, and scaling (not clamping) keeps its sunlit lumps lighter than its shade */
export const V_MUL: Record<string, number> = { tree_maple: 0.88, tree_ash: 0.88, tree_elm: 0.9 };

function hsvClamp(r: number, g: number, b: number, sMax: number, vMax = 1): [number, number, number] {
  const mx = Math.max(r, g, b), mn = Math.min(r, g, b);
  if (mx === 0) return [r, g, b];
  const s = (mx - mn) / mx, v = mx / 255;
  const s2 = Math.min(s, sMax), v2 = Math.min(v, vMax);
  if (s2 === s && v2 === v) return [r, g, b];
  // keep hue: move each channel toward the max by the saturation ratio, then scale by the value ratio
  const k = s > 0 ? s2 / s : 1, vk = v2 / v;
  const f = (c: number) => (mx - (mx - c) * k) * vk;
  return [f(r), f(g), f(b)];
}

const magenta = (r: number, g: number, b: number) => r > g + 40 && b > g + 25 && r > 120;
/** keying remnants inside foliage: a pink (red high, blue at or above green) that no autumn leaf has; tree_ash
 *  carried 144 of these (245,126,131) inside the crown, where the edge test cannot see them */
const pink = (r: number, g: number, b: number) => r > 170 && r - g > 70 && b >= g - 5;
const FOLIAGE = /^(tree_|shrub|planter)/;
/** R10-037 (round 9): the Black Dog deck's lattice (white slats, red vines) is the first thing seen upstairs and carried a pink
 *  fringe: slats blended with the vine reds into a pale pink (hue 300-10, S 0.08-0.4, V >= 0.8) no white paint has. Those go
 *  back to the slats' own warm white at their value, and its vines are folded onto one key hue like a crown's */
const VINES = /^lattice$/;
const palePink = (r: number, g: number, b: number) => {
  const mx = Math.max(r, g, b), mn = Math.min(r, g, b); if (mx < 204 || mx === mn) return false;
  const s = (mx - mn) / mx, h = hueOf(r, g, b); return s >= 0.06 && s <= 0.42 && (h >= 295 || h <= 12);
};

/** a crown's off-key specks (round 4): the image model sprinkles a few colours no leaf of that tree has - salmon dots in
 *  a yellow elm (234,125,94: 246 px), purple dots in a spruce (5 colours, 468 px). A hue that is rare in the crown (< 5%
 *  of its pixels) and more than 20 degrees from the crown's key hue is folded onto the key hue, keeping its saturation and
 *  (a step darker) its value, so the shading stays and the sparkle goes. Common second hues (a maple's oranges) stay. */
function foldCrown(d: Uint8ClampedArray, at: (x: number, y: number) => number, fw: number, fh: number) {
  const bins = new Array(24).fill(0); let tot = 0;
  for (let y = 0; y < fh; y++) for (let x = 0; x < fw; x++) {
    const o = at(x, y); if (d[o + 3] < 128 || Math.max(d[o], d[o + 1], d[o + 2]) < 64) continue;
    bins[Math.floor(hueOf(d[o], d[o + 1], d[o + 2]) / 15) % 24]++; tot++;
  }
  if (tot < 50) return;
  const key = bins.indexOf(Math.max(...bins)) * 15 + 7.5;
  for (let y = 0; y < fh; y++) for (let x = 0; x < fw; x++) {
    const o = at(x, y); if (d[o + 3] < 128) continue;
    const r = d[o], g = d[o + 1], b = d[o + 2], mx = Math.max(r, g, b), mn = Math.min(r, g, b);
    if (mx < 64 || mx - mn < 0.1 * mx) continue;
    const h = hueOf(r, g, b); const dh = Math.min(Math.abs(h - key) % 360, 360 - Math.abs(h - key) % 360);
    if (dh <= 20 || bins[Math.floor(h / 15) % 24] >= 0.05 * tot) continue;
    const [nr, ng, nb] = hsv2rgb(key, (mx - mn) / mx, mx / 255 * 0.92);
    d[o] = nr; d[o + 1] = ng; d[o + 2] = nb;
  }
}
function hueOf(r: number, g: number, b: number): number {
  const mx = Math.max(r, g, b), mn = Math.min(r, g, b), dd = mx - mn; if (!dd) return 0;
  let h = mx === r ? ((g - b) / dd) % 6 : mx === g ? (b - r) / dd + 2 : (r - g) / dd + 4;
  h *= 60; return h < 0 ? h + 360 : h;
}
function hsv2rgb(h: number, s: number, v: number): [number, number, number] {
  const c = v * s, x = c * (1 - Math.abs((h / 60) % 2 - 1)), m = v - c;
  const [r, g, b] = h < 60 ? [c, x, 0] : h < 120 ? [x, c, 0] : h < 180 ? [0, c, x] : h < 240 ? [0, x, c] : h < 300 ? [x, 0, c] : [c, 0, x];
  return [(r + m) * 255, (g + m) * 255, (b + m) * 255];
}

/** grade one frame in place: d is the page's RGBA, w the page width */
export function gradeFrame(d: Uint8ClampedArray, w: number, x0: number, y0: number, fw: number, fh: number, key: string) {
  const at = (x: number, y: number) => ((y0 + y) * w + (x0 + x)) * 4;
  const solid = (x: number, y: number) => x >= 0 && y >= 0 && x < fw && y < fh && d[at(x, y) + 3] >= 128;
  const edge0 = (x: number, y: number) => !solid(x + 1, y) || !solid(x - 1, y) || !solid(x, y + 1) || !solid(x, y - 1);
  // defringe (edges everywhere; inside foliage too)
  const leafy = FOLIAGE.test(key);
  for (let y = 0; y < fh; y++) for (let x = 0; x < fw; x++) {
    const o = at(x, y); if (d[o + 3] < 128) continue;
    const edge = !solid(x + 1, y) || !solid(x - 1, y) || !solid(x, y + 1) || !solid(x, y - 1);
    const bad = (edge && magenta(d[o], d[o + 1], d[o + 2])) || (leafy && pink(d[o], d[o + 1], d[o + 2]))
      || (leafy && Math.min(d[o], d[o + 1], d[o + 2]) > 150);   // near-white specks, on the rim and inside (elm 46, ash 68, spruce 116)
    if (!bad) continue;
    let r = 0, g = 0, b = 0, n = 0;
    for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1], [1, 1], [-1, -1], [1, -1], [-1, 1]]) {
      if (!solid(x + dx, y + dy)) continue; const q = at(x + dx, y + dy);
      if (magenta(d[q], d[q + 1], d[q + 2]) || (leafy && (pink(d[q], d[q + 1], d[q + 2]) || Math.min(d[q], d[q + 1], d[q + 2]) > 150))) continue;
      r += d[q]; g += d[q + 1]; b += d[q + 2]; n++;
    }
    if (n) { d[o] = r / n * 0.85; d[o + 1] = g / n * 0.85; d[o + 2] = b / n * 0.85; }   // a shade darker: an edge, not a highlight
    else d[o + 3] = 0;
  }
  if (VINES.test(key)) for (let y = 0; y < fh; y++) for (let x = 0; x < fw; x++) {
    const o = at(x, y); if (d[o + 3] < 128) continue;
    if (edge0(x, y) && magenta(d[o], d[o + 1], d[o + 2])) { d[o + 3] = 0; continue; }   // the keying matte on the cut edge
    if (palePink(d[o], d[o + 1], d[o + 2])) { const v = Math.max(d[o], d[o + 1], d[o + 2]); d[o] = v * 0.97; d[o + 1] = v * 0.94; d[o + 2] = v * 0.88; }
  }
  if (leafy || VINES.test(key)) foldCrown(d, at, fw, fh);
  const sMax = SAT_CEIL[key];
  if (sMax === undefined) return;
  const vMul = V_MUL[key] ?? 1;
  for (let y = 0; y < fh; y++) for (let x = 0; x < fw; x++) {
    const o = at(x, y); if (d[o + 3] < 128) continue;
    const [r, g, b] = hsvClamp(d[o] * vMul, d[o + 1] * vMul, d[o + 2] * vMul, sMax);
    d[o] = r; d[o + 1] = g; d[o + 2] = b;
  }
}

/** a repainted car body (IsoArt.car): STYLE H10 cars keep S <= 0.55 and V <= 0.86 after the tint, so the brightened
 *  highlights of an orange or purple paint job do not turn the car into the loudest thing on the street */
export function carPaint(r: number, g: number, b: number): [number, number, number] {
  return hsvClamp(r, g, b, 0.55, 0.86);
}

/** STYLE H10 ground: a swatch whose pattern is louder than its neighbours is pulled toward its own mean.
 *  ground:alley's dark gravel blotches (stddev 14.6 against 8.9-12.8 for lot, sidewalk and pavers) read as camouflage
 *  next to the quiet sidewalk; 0.35 brings it to about 9.5 */
// cycle 011 round 2: the lot's oil blotches made a polka-dot field over a whole back lot; calmed like the alley's
export const TEX_CALM: Record<string, number> = { 'ground:alley': 0.35, 'ground:lot': 0.45 };
// cycle 011 round 2 (Matt 2026-10-31: "poorly painted textures"): the grass swatch was a summer yellow-green the painted
// blocks never use; moved to the dead late-October olive of the painted verges (mean colour, texture kept)
export const TEX_MEAN: Record<string, [number, number, number]> = { 'ground:grass': [92, 90, 62] };
export function gradeTex(name: string, d: Uint8ClampedArray) {
  const k = TEX_CALM[name], to = TEX_MEAN[name]; if (!k && !to) return;
  let r = 0, g = 0, b = 0, n = 0;
  for (let i = 0; i < d.length; i += 4) if (d[i + 3] >= 128) { r += d[i]; g += d[i + 1]; b += d[i + 2]; n++; }
  if (!n) return; r /= n; g /= n; b /= n;
  if (k) for (let i = 0; i < d.length; i += 4) { d[i] += (r - d[i]) * k; d[i + 1] += (g - d[i + 1]) * k; d[i + 2] += (b - d[i + 2]) * k; }
  if (to) for (let i = 0; i < d.length; i += 4) { d[i] *= to[0] / r; d[i + 1] *= to[1] / g; d[i + 2] *= to[2] / b; }
}

/** STYLE H10 buildings: a wall material with no art swatch falls back to one flat colour per face. 'glass' (three
 *  buildings, among them Black Ice Coffee) read as a pale blue slab (stddev 4.7, hue 198) - a near-YOU colour and a
 *  greybox look. Synthesise a curtain-wall swatch in the same 2 m per 128 px scale as the painted ones: dark glass
 *  with a diagonal sheen, mullions every 0.5 m, a transom every 1 m, a spandrel band at the top of each 2 m. */
export function synthTex(name: string): { w: number; h: number; d: Uint8ClampedArray } | null {
  if (name !== 'walls:glass') return null;
  const w = 128, h = 128, d = new Uint8ClampedArray(w * h * 4);
  const GLASS = [46, 66, 80], HI = [92, 118, 132], FRAME = [30, 31, 34], SPAN = [58, 60, 64];
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    let c: number[];
    if (y < 10) c = SPAN;                                       // spandrel (floor edge)
    else if (x % 32 < 3 || y % 64 < 3) c = FRAME;               // mullions and transoms
    else {
      const t = ((x + y * 0.6) % 48) / 48;                      // a soft diagonal reflection band per pane
      const k = t < 0.18 ? 0.55 : t < 0.26 ? 0.25 : 0;
      c = GLASS.map((g, i) => g + (HI[i] - g) * k);
      if ((x * 7 + y * 13) % 29 === 0) c = c.map(v => v + 10);   // a little grime, so it is not perfectly clean
    }
    const o = (y * w + x) * 4; d[o] = c[0]; d[o + 1] = c[1]; d[o + 2] = c[2]; d[o + 3] = 255;
  }
  return { w, h, d };
}
